import { baseClient } from "./baseRequest";
import { Gift } from "./giftCategories";

export enum CollectionProcessingStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  PARTIAL_FAILURE = "partial_failure",
}

export type OrderClock = {
  clockId?: string;
  sku?: string;
  affiliateCollectionId?: string;
  affiliatePayoutAt?: Date;
  affiliateCollectionGrantedAt?: Date;
};

export type Order = {
  id: string;
  orderName: string;
  clocks: OrderClock[];
  orderCreationDate: Date;
  clocksNumber: number;
  customerEmail: string;
  customerName: string;
  collections: Array<{
    collectionId: string;
    code?: string;
    processedAt?: Date;
  }>;
  collectionEmailSent: boolean;
  collectionProcessingStatus: CollectionProcessingStatus;
  cancelled: boolean;
  fulfilled: boolean;
  collectionProcessedAt?: Date;
  manualOverride: boolean;
  comment?: string;
  createdAt?: Date;
  updatedAt?: Date;
};

export type OrderWithGift = Order & {
  gift: Gift;
};

export type SearchOrdersInput = {
  query?: string;
  limit?: number;
  skip?: number;
  sort?: Record<string, 1 | -1>;
  filters?: Record<string, unknown>;
  populateGift?: boolean;
};

export type OrdersClient = {
  textSearchOrders(
    input: SearchOrdersInput,
  ): Promise<Order[] | OrderWithGift[]>;
  getOrderById(
    id: string,
    populateGift?: boolean,
  ): Promise<Order | OrderWithGift>;
  updateOrderManualOverride(
    id: string,
    manualOverride: boolean,
  ): Promise<Order>;
  updateOrderClocks(
    id: string,
    clocks: OrderClock[],
    clocksNumber?: number,
  ): Promise<Order>;
  updateOrderComment(id: string, comment: string): Promise<Order>;
};

const ordersClient: OrdersClient = {
  textSearchOrders(
    input: SearchOrdersInput,
  ): Promise<Order[] | OrderWithGift[]> {
    const { query = "", limit, skip, sort, filters, populateGift } = input;
    return baseClient.post<Order[] | OrderWithGift[]>({
      url: `/admin/orders/.search?query=${encodeURIComponent(query)}`,
      body: { limit, skip, sort, filters, populateGift },
    });
  },

  getOrderById(
    id: string,
    populateGift?: boolean,
  ): Promise<Order | OrderWithGift> {
    return baseClient.get<Order | OrderWithGift>({
      url: `/admin/orders/${id}`,
      params: populateGift ? { populateGift: "true" } : undefined,
    });
  },

  updateOrderManualOverride: async (id: string, manualOverride: boolean) => {
    return baseClient.put<Order>({
      url: `/admin/orders/${id}/manual-override`,
      body: { manualOverride },
    });
  },

  updateOrderClocks: async (
    id: string,
    clocks: OrderClock[],
    clocksNumber?: number,
  ) => {
    return baseClient.put<Order>({
      url: `/admin/orders/${id}/clocks`,
      body: { clocks, clocksNumber },
    });
  },

  updateOrderComment: async (id: string, comment: string) => {
    return baseClient.put<Order>({
      url: `/admin/orders/${id}/comment`,
      body: { comment },
    });
  },
};

export default ordersClient;
